import { useEffect, useMemo, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { useModalContext } from "../../../context/ModalContext";
import {
  abortFindResult,
  closeFindResult,
  createFindResult,
} from "../api/find-dialog";
import {
  IFindDeviceFoundItem,
  IFindDeviceParams,
} from "../api/types/find-dialog.interface";
import { useToast } from "../../../context/toast/ui";

export function useFindDevice() {
  const { control, handleSubmit } = useForm();
  const { closeModal } = useModalContext();
  const { showMessage } = useToast();
  const [activateChecked, setActivateChecked] = useState<boolean>(true);
  const [showAddedChecked, setShowAddedChecked] = useState<boolean>(false);
  const [findData, setFindData] = useState<IFindDeviceParams | null>(null);
  const [progress, setProgress] = useState(0);
  const [founded, setFounded] = useState<IFindDeviceFoundItem[]>([]);
  const [selected, setSelected] = useState<IFindDeviceFoundItem[]>([]);

  useEffect(() => {
    if (!findData) return;

    if (findData.status === "finish") {
      setProgress(100);
    } else {
      setProgress(findData.pos);
    }
  }, [findData]);

  useEffect(() => {
    if (!findData) return;
    if (findData.found) {
      const newFounded = findData.found;
      setFounded((prevFounded) => {
        const selectedItems = newFounded.filter(
          (item: IFindDeviceFoundItem) => item.added
        );
        setSelected((prevSelected) => [...prevSelected, ...selectedItems]);

        return [...prevFounded, ...newFounded];
      });
    }
  }, [findData]);

  const filteredFoundedOptions = useMemo(() => {
    return showAddedChecked
      ? [...founded]
      : founded.filter((item) => !item.added);
  }, [founded, showAddedChecked]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleCloseFindDialog = async () => {
    const abortResponse = await abortFindResult();
    if (abortResponse) {
      const closeResponse = await closeFindResult();
      if (closeResponse) {
        closeModal();
      }
    }
  };

  const onSubmit = handleSubmit(async (data: FieldValues) => {
    const abort = await abortFindResult();

    if (!abort) return;

    const fields: {
      [key: string]: {
        type_id: number;
        caption: string;
      };
    } = data;

    const requestData = selected
      .filter((item) => !item.added)
      .map((item) => {
        if (fields[item.id]) {
          return {
            id: item.id,
            type_id: fields[item.id].type_id,
          };
        }

        return null;
      })
      .filter((item): item is { id: number; type_id: number } => item !== null); // Удаление null значений и приведение типа

    if (!requestData) return;

    try {
      const response = await createFindResult({
        activate: activateChecked,
        data: requestData,
      });
      if (response) {
        closeModal();
      }
    } catch (error) {
      const abortResponse = await abortFindResult();
      if (abortResponse) {
        const closeResponse = await closeFindResult();
        if (closeResponse) {
          closeModal();
          showMessage({
            message: String(error),
            type: "error",
            caption: "Ошибка добавления устройств",
            tag: "find-device",
            timeOut: 5,
          });
        }
      }
    }
  });

  return useMemo(
    () => ({
      control,
      activateChecked,
      setActivateChecked,
      showAddedChecked,
      setShowAddedChecked,
      findData,
      setFindData,
      progress,
      founded,
      selected,
      filteredFoundedOptions,
      handleCloseFindDialog,
      onSubmit,
      setSelected,
    }),
    [
      activateChecked,
      control,
      filteredFoundedOptions,
      findData,
      founded,
      handleCloseFindDialog,
      onSubmit,
      progress,
      selected,
      showAddedChecked,
    ]
  );
}
